import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { take } from 'rxjs';
import { DateRangeSelectorComponent } from '../../../basic/date-range-selector/date-range-selector.component';
import { BoardService } from '../../../core/board/board.service';
import { SessionTemplate } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { TaskService } from '../../../core/task/task.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { surveyAnimations } from '../../../shared/animation/survey.animation';
import { BoardIconTitleComponent } from '../../board/board-icon-title/board-icon-title.component';
import { BoardVerticalIconTitleComponent } from '../../board/board-vertical-icon-title/board-vertical-icon-title.component';
import { SessionTemplateCardComponent } from '../../session-template/session-template-card/session-template-card.component';

@Component({
  selector: 'app-new-scenario-task',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,
    NzPopconfirmModule,
    NzDividerModule,
    NzProgressModule,
    NzCheckboxModule,
    NzToolTipModule,
    MatTooltipModule,
    NzSkeletonModule,

    DateRangeSelectorComponent,

    BoardVerticalIconTitleComponent,
    BoardIconTitleComponent,
    SessionTemplateCardComponent,
  ],
  templateUrl: './new-scenario-task.component.html',
  styleUrl: './new-scenario-task.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: surveyAnimations,
})
export class NewScenarioTaskComponent {
  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  isLoading = signal<boolean>(false);

  currentStepIndex = signal<number>(0);

  readonly totalSteps = 4;
  currentProgress = computed(() => {
    return Math.round((this.currentStepIndex() / this.totalSteps) * 100);
  });

  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });

  userList = toSignal(this.boardService.getAllUserList$(), { initialValue: [] });

  leaderboardName: string = '';

  constructor(
    private sessionTemplateService: SessionTemplateService,
    private boardService: BoardService,
    private taskService: TaskService,
    private messageService: NzMessageService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  onOnboardingClick() {
    this.leaderboardName = 'Onboarding';
  }

  selectableSessionTemplateList = computed(() => {
    const itemList = this.sessionTemplateList();
    return itemList.map((item) => {
      return {
        isSelected: false,
        item,
      };
    });
  });

  selectableUserList = computed(() => {
    const itemList = this.userList();
    return itemList.map((item) => {
      return {
        isSelected: false,
        item,
      };
    });
  });

  disabledAfter = signal<DateTime | undefined>(undefined);

  onStartOrEndChange(dateRange: { startDate?: DateTime; endDate?: DateTime }) {
    console.log('dateRange', dateRange);
    if (dateRange?.startDate) {
      this.disabledAfter.set(dateRange.startDate.plus({ months: 3 }));
    }
  }

  dateRange?: { startDate?: DateTime; endDate?: DateTime };

  onDateRangeChange(dateRange: { startDate?: DateTime; endDate?: DateTime }) {
    this.dateRange = dateRange;
  }

  onInOneWeekClick() {
    this.dateRange = {
      startDate: DateTime.now().startOf('day'),
      endDate: DateTime.now().startOf('day').plus({ days: 7 }),
    };
  }

  onInOneMonthClick() {
    this.dateRange = {
      startDate: DateTime.now().startOf('day'),
      endDate: DateTime.now().startOf('day').plus({ months: 1 }),
    };
  }

  onSessionTemplateCardClick(selectableItem: { isSelected: boolean; item: SessionTemplate }) {
    selectableItem.isSelected = !selectableItem.isSelected;
  }

  onUserClick(selectableItem: { isSelected: boolean }) {
    selectableItem.isSelected = !selectableItem.isSelected;
  }

  onAddTaskClick() {
    this.currentStepIndex.set(this.currentStepIndex() + 1);

    this.isLoading.set(true);

    const selectedSessionTemplateList = this.selectableSessionTemplateList().filter((item) => item.isSelected);
    const selectedUserList = this.selectableUserList().filter((item) => item.isSelected);

    if (selectedSessionTemplateList.length === 0) {
      this.messageService.error('Please select at least one senario.', { nzDuration: 10000 });
      return;
    }

    if (selectedUserList.length === 0) {
      this.messageService.error('Please select at least one member.', { nzDuration: 10000 });
      return;
    }

    if (!this.dateRange || !this.dateRange?.startDate || !this.dateRange?.endDate) {
      this.messageService.error('Please select start and end dates to continue.', { nzDuration: 10000 });
      return;
    }

    return this.taskService
      .addTargetTask$(
        this.leaderboardName,
        this.dateRange!.startDate!,
        this.dateRange!.endDate!,
        selectedSessionTemplateList.map((item) => item.item.id),
        selectedUserList.map((item) => item.item.id)
      )
      .pipe(take(1))
      .subscribe(() => {
        this.isLoading.set(false);
        this.isVisible = false;
        this.isVisibleChange.emit(false);
      });
  }

  onPreviousClick() {
    this.currentStepIndex.set(this.currentStepIndex() - 1);
  }

  onNextClick() {
    if ((this.currentStepIndex() === 0 && !this.dateRange) || !this.dateRange?.startDate || !this.dateRange?.endDate) {
      this.messageService.error('Please select start and end dates to continue.', { nzDuration: 10000 });
      return;
    }

    if (this.currentStepIndex() === 1 && !this.leaderboardName) {
      this.messageService.error('Please type in a leaderboard name.', { nzDuration: 10000 });
      return;
    }

    if (this.currentStepIndex() === 2 && !this.selectableSessionTemplateList().some((item) => item.isSelected)) {
      this.messageService.error('Please select at least one senario.', { nzDuration: 10000 });
      return;
    }

    if (this.currentStepIndex() === 3 && !this.selectableUserList().some((item) => item.isSelected)) {
      this.messageService.error('Please select at least one member.', { nzDuration: 10000 });
      return;
    }

    this.currentStepIndex.set(this.currentStepIndex() + 1);
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }
}
